import { t } from '@i18n';
import {
  AnalyzeBuildingCrremFragment,
  AnalyzeBuildingNationalEnergyLawFragment,
  AnalyzeBuildingsCrremAnalysisResultFragment,
  AnalyzeBuildingsNationalEnergyLawAnalysisResultFragment,
  BuildingsBenchmarkCrremFragment,
  BuildingsBenchmarkNationalEnergyLawFragment,
  ConsumptionAllocationCrremCarbonEmissionFragment,
  StatusQuoAnalysisBuildingBasicDetailsFragment,
  StatusQuoAnalysisGetAnalyzedBuildingsCoreQuery,
  StatusQuoAnalysisGetBenchmarkQuery,
} from '@predium/client-graphql';
import {
  analysis_reference_area_enum,
  area_type_enum,
  class_of_use_enum,
  country_enum,
  emission_factor_type_enum,
  org_sector_type_enum,
  type_of_use_enum,
} from '@predium/enums';
import {
  translateAnalysisReferenceAreaEnum_dynamic,
  translateClassOfUseEnum_dynamic,
  translateCountryEnum,
  translateTypeOfUseEnum_dynamic,
} from '@predium/i18n/client';
import { accessEnum, getEbfClassesOfUse, getEbfTypesOfUse, getNetArea, Localize, Units } from '@predium/utils';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { PageHeaderMetaItemProps } from '@/components/organisms/PageHeader/PageHeaderMetaItem';
import { PreLink } from '@/components/presentations/PreLink';
import { PATHS } from '@/routes';
import { CurrentPageView } from '@/sections/StatusQuoAnalysis/Portfolio/PortfolioAnalysisHeader';

type AreasForTypeOfUse = Parameters<typeof getEbfTypesOfUse>[0] & Parameters<typeof getEbfClassesOfUse>[0];

const getCountryItem = ({ country }: { country: country_enum | undefined }): PageHeaderMetaItemProps => ({
  type: 'country',
  label: t('General_Country'),
  value: isNil(country) ? '' : translateCountryEnum(country),
});

const getCountriesItem = ({ countries }: { countries: country_enum[] }): PageHeaderMetaItemProps | undefined => {
  if (countries.length === 0) return undefined;
  return {
    type: 'country',
    label: t('General_Country'),
    value:
      countries.length === 1 ? translateCountryEnum(countries[0]) : t('StatusQuoAnalysisSection_MultipleCountries'),
  };
};

const getPortfolioLinkItem = ({
  portfolioId,
  portfolioName,
}: {
  portfolioId: number | undefined;
  portfolioName: string | undefined;
}): PageHeaderMetaItemProps => ({
  type: 'portfolio',
  label: t('General_Portfolio'),
  value: (
    <PreLink
      link={{
        href: PATHS.statusQuoAnalysis.portfolio({ portfolioIds: [Number(portfolioId)] }),
        name: portfolioName ?? '',
      }}
      typographyProps={{ color: 'text.secondary', variant: 'caption' }}
    />
  ),
});

const getPortfolioTextItem = ({ portfolioName }: { portfolioName: string | undefined }): PageHeaderMetaItemProps => ({
  type: 'portfolio',
  label: t('General_Portfolio'),
  value: portfolioName ?? '',
});

const getEconomicUnitLinkItem = ({
  economicUnitId,
  economicUnitName,
  orgSector,
}: {
  economicUnitId: number | undefined;
  economicUnitName: string | undefined;
  orgSector: org_sector_type_enum;
}): PageHeaderMetaItemProps => ({
  type: 'economicUnit',
  label: t('General_EconomicUnit', { count: 1, orgSector }),
  value: (
    <PreLink
      link={{
        href: PATHS.statusQuoAnalysis.economicUnits({ economicUnitIds: [Number(economicUnitId)] }),
        name: economicUnitName ?? '',
      }}
      typographyProps={{ color: 'text.secondary', variant: 'caption' }}
    />
  ),
});

const getEconomicUnitTextItem = ({
  economicUnitName,
  orgSector,
}: {
  economicUnitName: string | undefined;
  orgSector: org_sector_type_enum;
}): PageHeaderMetaItemProps => ({
  type: 'economicUnit',
  label: t('General_EconomicUnit', { count: 1, orgSector }),
  value: economicUnitName ?? '',
});

const getTypeOfUseItem = ({ areas }: { areas: AreasForTypeOfUse }): PageHeaderMetaItemProps => {
  const typesOfUse = getEbfTypesOfUse(areas);
  const isCommercial = getEbfClassesOfUse(areas).has(class_of_use_enum.COMMERCIAL);
  return {
    type: isCommercial ? 'classOfUseCommercial' : 'classOfUseResidential',
    label: t('General_TypeOfUse'),
    value: typesOfUse.map((type: type_of_use_enum) => translateTypeOfUseEnum_dynamic(type)).join(', '),
  };
};

const getResidentialShareItem = ({
  percentage,
  localize,
}: {
  percentage: number;
  localize: Localize;
}): PageHeaderMetaItemProps => ({
  type: 'classOfUseResidential',
  label: t('General_ShareResidential'),
  value: `${localize.formatAsPercentage(percentage)} ${translateClassOfUseEnum_dynamic(class_of_use_enum.RESIDENTIAL)}`,
});

const getCommercialShareItem = ({
  percentage,
  localize,
}: {
  percentage: number;
  localize: Localize;
}): PageHeaderMetaItemProps => ({
  type: 'classOfUseCommercial',
  label: t('General_ShareCommercial'),
  value: `${localize.formatAsPercentage(percentage)} ${translateClassOfUseEnum_dynamic(class_of_use_enum.COMMERCIAL)}`,
});

const getConstructionYearItem = ({ year }: { year: number | null | undefined }): PageHeaderMetaItemProps => ({
  type: 'constructionYear',
  label: t('General_ConstructionYear'),
  value: year,
});

const getFloorAreaItem = ({
  area,
  referenceAreaType,
  localize,
}: {
  area: number;
  referenceAreaType: analysis_reference_area_enum;
  localize: Localize;
}): PageHeaderMetaItemProps => ({
  type: 'floorArea',
  label: translateAnalysisReferenceAreaEnum_dynamic(referenceAreaType),
  value: localize.formatAsFloat(area, { unit: Units.area }),
});

const getTotalFloorAreaItem = ({
  area,
  referenceAreaType,
  localize,
}: {
  area: number;
  referenceAreaType: analysis_reference_area_enum;
  localize: Localize;
}): PageHeaderMetaItemProps => ({
  type: 'totalFloorArea',
  label: translateAnalysisReferenceAreaEnum_dynamic(referenceAreaType),
  value: localize.formatAsFloat(area, { unit: Units.area }),
});

const getBuildingValueItem = ({
  value,
  localize,
}: {
  value: number | null | undefined;
  localize: Localize;
}): PageHeaderMetaItemProps | undefined => {
  if (isNil(value)) return undefined;
  return {
    type: 'buildingValue',
    label: t('General_CurrentBuildingValue'),
    value: localize.formatAsCompact(value, { fractionDigits: 2 }),
  };
};

const getTotalBuildingsItem = ({
  count,
  localize,
}: {
  count: number;
  localize: Localize;
}): PageHeaderMetaItemProps => ({
  type: 'buildingCount',
  label: t('StatusQuoAnalysisSection_TotalBuildings'),
  value: localize.formatAsInteger(count),
});

const getResponsibleUserItem = ({
  user,
}: {
  user: { first_name?: string | null; last_name?: string | null } | null | undefined;
}): PageHeaderMetaItemProps | undefined => {
  if (isNil(user)) return undefined;
  return {
    type: 'responsibleUser',
    label: t('General_ResponsibleUser'),
    value: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
  };
};

const computeClassOfUseShares = (floorAreaByBuildingClass: FloorAreaByClassOfUse) => {
  const residential = floorAreaByBuildingClass.find((group) => group.classOfUse === 'RESIDENTIAL');
  const commercial = floorAreaByBuildingClass.find((group) => group.classOfUse === 'COMMERCIAL');
  const totalArea = (residential?.area ?? 0) + (commercial?.area ?? 0);
  return {
    residentialPercentage: residential ? (residential.area / totalArea) * 100 : 0,
    commercialPercentage: commercial ? (commercial.area / totalArea) * 100 : 0,
    totalArea,
  };
};

export const getSingleUseBuildingMetadata = ({
  building,
  orgSector,
  referenceAreaType,
  localize,
  isEconomicUnitsToggled,
}: {
  building: StatusQuoAnalysisBuildingBasicDetailsFragment | undefined;
  localize: Localize;
  referenceAreaType: analysis_reference_area_enum;
  orgSector: org_sector_type_enum;
  isEconomicUnitsToggled: boolean;
}): PageHeaderMetaItemProps[] => {
  const items: (PageHeaderMetaItemProps | undefined)[] = [
    getCountryItem({ country: building?.address.country_id }),
    getPortfolioLinkItem({
      portfolioId: building?.economic_unit.portfolio.id,
      portfolioName: building?.economic_unit.portfolio.name,
    }),
    isEconomicUnitsToggled
      ? getEconomicUnitLinkItem({
          economicUnitId: building?.economic_unit.id,
          economicUnitName: building?.economic_unit.name,
          orgSector,
        })
      : undefined,
    getTypeOfUseItem({ areas: building?.areas }),
    getConstructionYearItem({ year: building?.year_constructed }),
    getFloorAreaItem({
      area: getNetArea(building?.areas, accessEnum(area_type_enum, referenceAreaType)),
      referenceAreaType,
      localize,
    }),
    getBuildingValueItem({ value: building?.building_valuations[0]?.value, localize }),
  ];

  return items.filter((item): item is PageHeaderMetaItemProps => !isNil(item));
};

export const getMixUseBuildingMetadata = ({
  building,
  floorAreaByBuildingClass,
  orgSector,
  referenceAreaType,
  localize,
  isEconomicUnitsToggled,
}: {
  building: StatusQuoAnalysisBuildingBasicDetailsFragment | undefined;
  floorAreaByBuildingClass: FloorAreaByClassOfUse;
  localize: Localize;
  referenceAreaType: analysis_reference_area_enum;
  orgSector: org_sector_type_enum;
  isEconomicUnitsToggled: boolean;
}): PageHeaderMetaItemProps[] => {
  const { residentialPercentage, commercialPercentage, totalArea } = computeClassOfUseShares(floorAreaByBuildingClass);

  const items: (PageHeaderMetaItemProps | undefined)[] = [
    getCountryItem({ country: building?.address.country_id }),
    getPortfolioLinkItem({
      portfolioId: building?.economic_unit.portfolio.id,
      portfolioName: building?.economic_unit.portfolio.name,
    }),
    isEconomicUnitsToggled
      ? getEconomicUnitLinkItem({
          economicUnitId: building?.economic_unit.id,
          economicUnitName: building?.economic_unit.name,
          orgSector,
        })
      : undefined,
    getResidentialShareItem({ percentage: residentialPercentage, localize }),
    getCommercialShareItem({ percentage: commercialPercentage, localize }),
    getConstructionYearItem({ year: building?.year_constructed }),
    getTotalFloorAreaItem({ area: totalArea, referenceAreaType, localize }),
  ];

  return items.filter((item): item is PageHeaderMetaItemProps => !isNil(item));
};

export type FloorAreaByClassOfUse =
  StatusQuoAnalysisGetAnalyzedBuildingsCoreQuery['getAnalyzedBuildingsCore']['floorAreaByClassOfUse'];

export const getPortfolioMetaInformation = ({
  floorAreaByBuildingClass,
  totalBuildings,
  localize,
  referenceAreaType,
  countries,
}: {
  floorAreaByBuildingClass: FloorAreaByClassOfUse;
  totalBuildings: number;
  localize: Localize;
  referenceAreaType: analysis_reference_area_enum;
  countries: country_enum[];
}): PageHeaderMetaItemProps[] => {
  const { residentialPercentage, commercialPercentage, totalArea } = computeClassOfUseShares(floorAreaByBuildingClass);

  const items: (PageHeaderMetaItemProps | undefined)[] = [
    getCountriesItem({ countries }),
    getTotalBuildingsItem({ count: totalBuildings, localize }),
    getResidentialShareItem({ percentage: Math.round(residentialPercentage), localize }),
    getCommercialShareItem({ percentage: Math.round(commercialPercentage), localize }),
    getTotalFloorAreaItem({ area: totalArea, referenceAreaType, localize }),
  ];

  return items.filter((item): item is PageHeaderMetaItemProps => !isNil(item));
};

type DataCollectionBuilding = {
  address: { country_id: country_enum };
  economic_unit: {
    id: number;
    name: string;
    portfolio: { id: number; name: string };
  };
  areas: AreasForTypeOfUse;
  year_constructed: number | null;
  responsible_user: { first_name?: string | null; last_name?: string | null } | null;
};

export const getDataCollectionBuildingMetadata = ({
  building,
  orgSector,
  isEconomicUnitsToggled,
}: {
  building: DataCollectionBuilding | undefined;
  orgSector: org_sector_type_enum;
  isEconomicUnitsToggled: boolean;
}): PageHeaderMetaItemProps[] => {
  const items: (PageHeaderMetaItemProps | undefined)[] = [
    getCountryItem({ country: building?.address.country_id }),
    getPortfolioTextItem({ portfolioName: building?.economic_unit.portfolio.name }),
    isEconomicUnitsToggled
      ? getEconomicUnitTextItem({ economicUnitName: building?.economic_unit.name, orgSector })
      : undefined,
    getTypeOfUseItem({ areas: building?.areas }),
    getConstructionYearItem({ year: building?.year_constructed }),
    getResponsibleUserItem({ user: building?.responsible_user }),
  ];

  return items.filter((item): item is PageHeaderMetaItemProps => !isNil(item));
};

/**
 * Returns the value or the absolute value depending on the isAbsolute flag.
 *
 * it accepts an object with a key and a keyAbsolute property.
 *
 * @param {boolean} isAbsolute - The flag to determine if the absolute value should be returned.
 * @param {object} obj - The object containing the key and keyAbsolute properties.
 * @param {string} key - The key to be used to get the value.
 *
 * @returns {number} The value or the absolute value.
 */
export const getValueOrAbsolute = <T extends Record<string, any>, K extends keyof T & string>(
  isAbsolute: boolean,
  obj: {
    [P in K]: number;
  } & {
    [P in `${K & string}Absolute`]: number;
  },
  key: K,
) => {
  if (isAbsolute) {
    return obj[`${key}Absolute`];
  }

  return obj[key];
};

export const isAnalyzeBuildingCrrem = (obj: any): obj is AnalyzeBuildingCrremFragment => {
  return obj.analysisType === emission_factor_type_enum.CRREM;
};
export const isAnalyzeBuildingNationalEnergyLaw = (obj: any): obj is AnalyzeBuildingNationalEnergyLawFragment => {
  return obj.analysisType === emission_factor_type_enum.NATIONAL_ENERGY_LAW;
};

export const isAnalyzeBuildingsAnalysisCrrem = (obj: any): obj is AnalyzeBuildingsCrremAnalysisResultFragment => {
  return obj.analysisType === emission_factor_type_enum.CRREM;
};
export const isAnalyzeBuildingsAnalysisNationalEnergyLaw = (
  obj: any,
): obj is AnalyzeBuildingsNationalEnergyLawAnalysisResultFragment => {
  return obj.analysisType === emission_factor_type_enum.NATIONAL_ENERGY_LAW;
};

export const isBuildingsBenchmarkCrrem = (obj: any): obj is BuildingsBenchmarkCrremFragment => {
  return obj.analysisType === emission_factor_type_enum.CRREM;
};
export const isBuildingsBenchmarkNationalEnergyLaw = (obj: any): obj is BuildingsBenchmarkNationalEnergyLawFragment => {
  return obj.analysisType === emission_factor_type_enum.NATIONAL_ENERGY_LAW;
};

export type Benchmark = StatusQuoAnalysisGetBenchmarkQuery['getBenchmark'];

// A benchmark comparison can be shown once either it's not requested, or the (possibly
// still-loading, hence undefined) benchmark has actually arrived and matches CRREM.
export const isCrremBenchmarkComparisonReady = (
  benchmark: Benchmark | null | undefined,
  isBenchmarkActive: boolean,
): boolean => !isBenchmarkActive || (!isNil(benchmark) && isBuildingsBenchmarkCrrem(benchmark));

export const calculateBenchmarkChange = (value: number, benchmarkValue?: number | null) => {
  if (isNil(benchmarkValue) || benchmarkValue === 0) {
    return null;
  }
  return (1 - value / benchmarkValue) * -1;
};

export const getBenchmarkAverageCo2Emissions = (benchmark?: Benchmark): number => {
  if (!isNil(benchmark) && isBuildingsBenchmarkCrrem(benchmark)) {
    return benchmark.crremAnalysis.averageCo2Emissions;
  }
  if (!isNil(benchmark) && isBuildingsBenchmarkNationalEnergyLaw(benchmark)) {
    return benchmark.nationalEnergyLawAnalysis.averageCo2Emissions;
  }
  return 0;
};

export const isConsumptionAllocationCrremCO2 = (obj: any): obj is ConsumptionAllocationCrremCarbonEmissionFragment => {
  return obj.analysisType === emission_factor_type_enum.CRREM;
};

export const getCumulativeExcessEmissionsTooltipText = (
  currentPageView: CurrentPageView,
  orgSector: org_sector_type_enum,
) => {
  switch (currentPageView) {
    case 'status-quo-portfolio':
      return t('StatusQuoAnalysis_Co2CrremAnalysis_ExcessEmissionsTooltip_Portfolio');
    case 'status-quo-economics-units':
      return t('StatusQuoAnalysis_Co2CrremAnalysis_ExcessEmissionsTooltip_EconomicUnit', { orgSector });
    case 'status-quo-building':
    default:
      return t('StatusQuoAnalysis_Co2CrremAnalysis_ExcessEmissionsTooltip');
  }
};

export const getStrandingDateTooltipText = ({
  orgSector,
  orgName,
  currentPageView,
  isCustomTargetPath,
  isV207 = false,
}: {
  orgSector: org_sector_type_enum;
  orgName?: string;
  currentPageView: CurrentPageView;
  isCustomTargetPath?: boolean;
  isV207?: boolean;
}) => {
  if (isCustomTargetPath) {
    return isV207
      ? t('StatusQuoAnalysis_Co2CrremAnalysis_MisalignmentDateTooltip_CustomTargetPath', { orgName })
      : t('StatusQuoAnalysis_Co2CrremAnalysis_StrandingDateTooltip_CustomTargetPath', { orgName });
  }

  switch (currentPageView) {
    case 'status-quo-portfolio':
      return isV207
        ? t('StatusQuoAnalysis_Co2CrremAnalysis_MisalignmentDateTooltip_Portfolio')
        : t('StatusQuoAnalysis_Co2CrremAnalysis_StrandingDateTooltip_Portfolio');
    case 'status-quo-economics-units':
      return isV207
        ? t('StatusQuoAnalysis_Co2CrremAnalysis_MisalignmentDateTooltip_EconomicUnit', { orgSector })
        : t('StatusQuoAnalysis_Co2CrremAnalysis_StrandingDateTooltip_EconomicUnit', { orgSector });
    case 'status-quo-building':
    default:
      return isV207
        ? t('StatusQuoAnalysis_Co2CrremAnalysis_MisalignmentDateTooltip')
        : t('StatusQuoAnalysis_Co2CrremAnalysis_StrandingDateTooltip');
  }
};

export const getYearRange = (year: number) => {
  const from = dayjs().year(year).startOf('year').valueOf();
  const to = dayjs().year(year).endOf('year').valueOf();
  return { from, to };
};

/**
 * Validates URL parameter IDs against loaded data and returns only valid IDs.
 * Used to remove invalid IDs from URL when the user pastes a URL with invalid IDs.
 */
export const validateUrlParameterIds = (urlIds: number[], loadedIds: number[]): number[] => {
  if (urlIds.length === 0) return [];
  return urlIds.filter((id) => loadedIds.includes(id));
};
